import { Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchChapterByPageNumber } from '../../utils/DocumentUtils';
import { useUserManagement } from '../../features/UserManagement/components/UserManagementContext';
import { useDocumentContext } from '../DocumentContext';
import { useSessionContext } from '../SessionContext';
import { useCallback, useState } from 'react';

type FormState = 'pending' | 'error' | 'ready';
export default function Form({
  lastPageNumber,
  onClose,
}: Readonly<{ lastPageNumber: string; onClose: () => void }>) {
  const { t } = useTranslation();
  const { id } = useDocumentContext();
  const { identity } = useUserManagement();
  const { navigateToChapter } = useSessionContext();
  const [state, setState] = useState<FormState>('ready');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const resetError = () => {
    if (state === 'error') {
      setState('ready');
      setErrorKey(null);
    }
  };

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setState('pending');
      setErrorKey(null);
      const formData = new FormData(e.currentTarget);
      const pageNumber = formData.get('pageNumber');

      if (!pageNumber || pageNumber === '') {
        setErrorKey('missingPageNumber');
        setState('error');
        return;
      }

      try {
        const positionChapters = await fetchChapterByPageNumber(
          id,
          pageNumber as string,
          await identity?.actions?.getAccessToken(),
        );

        const targetChapter = positionChapters[0];

        await navigateToChapter({
          chapter: targetChapter,
          pageNumber: pageNumber as string,
        });
        onClose();
      } catch (_error) {
        setErrorKey('pagePositionNotFound');
        setState('error');
      }
    },
    [navigateToChapter, onClose, id, identity?.actions],
  );

  return (
    <Stack
      component='form'
      onSubmit={onSubmit}
      sx={{ maxWidth: 240, width: '100%', marginX: 'auto' }}
      gap={{ xs: 3, md: 2 }}
    >
      <TextField
        name='pageNumber'
        label={t('readingView.navigation.page.input.label')}
        variant='outlined'
        onChange={resetError}
        fullWidth
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            sx: {
              '&::after': {
                content: `"/${lastPageNumber}"`,
                paddingRight: '14px',
                color: 'text.secondary',
                textWrap: 'nowrap',
              },
            },
          },
        }}
      />
      <Stack
        direction={{ xs: 'column', md: 'row-reverse' }}
        gap={{ xs: 1, md: 2 }}
      >
        <Button
          type='submit'
          fullWidth
          size='large'
          variant='contained'
          color='primary'
          loading={state === 'pending'}
          loadingPosition='end'
          sx={{ textTransform: 'none' }}
        >
          {t('readingView.navigation.page.submit')}
        </Button>
        <Button
          type='button'
          fullWidth
          size='large'
          variant='outlined'
          color='inherit'
          sx={{ textTransform: 'none' }}
          onClick={onClose}
        >
          {t('readingView.navigation.page.cancel')}
        </Button>
      </Stack>
      {state === 'error' && (
        <Typography color='error' variant='caption' aria-live='polite'>
          {t(`readingView.navigation.page.error.${errorKey ?? 'default'}`)}
        </Typography>
      )}
    </Stack>
  );
}
